import DataTableCard from '../../../../components/ui/DataTableCard';
import { formatDateTime } from '../../../../utils/tableTemplates';
import AppCard from '../../../../components/ui/AppCard';
import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { getBeneficiaryChangeRequests, approveBeneficiaryChangeRequest, rejectBeneficiaryChangeRequest } from '../../../../services/beneficiary-service';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const BeneficiaryRequestsTable = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
    const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
    const [actionType, setActionType] = useState(null); // 'APPROVE' or 'REJECT'
    const [comments, setComments] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const toast = useRef(null);




    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await getBeneficiaryChangeRequests();
            if (response.data.status === "SUCCESS" || response.data.status === "success" || Array.isArray(response.data.responseBody)) {
                setRequests(response.data.responseBody || response.data);
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error('Error fetching beneficiary requests:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.status === 403 
                        ? 'Forbidden: You do not have permission to view change requests.' 
                        : 'Failed to load change requests.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleActionOpen = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setComments('');
        setReviewDialogVisible(true);
    };

    const handleReviewSubmit = async () => {
        if (!selectedRequest) return;

        // Validation: comment is required for rejection
        if (actionType === 'REJECT' && !comments.trim()) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Validation Error',
                    detail: 'Please provide a reason or comments for rejecting this request.'
                });
            }
            return;
        }

        setSubmitting(true);
        const payload = {
            comments: comments,
            reason: comments,
            reviewerSignature: 'System Reviewer'
        };

        try {
            let response;
            const reqId = selectedRequest.requestId || selectedRequest.id;
            if (actionType === 'APPROVE') {
                response = await approveBeneficiaryChangeRequest(reqId, payload);
            } else {
                response = await rejectBeneficiaryChangeRequest(reqId, payload);
            }

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.message || `Request successfully ${actionType.toLowerCase()}d.`
                });
            }
            setReviewDialogVisible(false);
            fetchRequests(); // Invalidate and refresh the list
        } catch (error) {
            console.error(`Error performing ${actionType} action:`, error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Action Failed',
                    detail: error.response?.status === 403 
                        ? 'Forbidden: You are not authorized to review requests.' 
                        : (error.response?.data?.message || 'Server error occurred during processing.')
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const resolveStatus = (rowData) => {
        if (rowData.approved) return 'APPROVED';
        if (rowData.rejected) return 'REJECTED';
        return 'PENDING';
    };

    const getStatusSeverity = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
                return 'success';
            case 'REJECTED':
                return 'danger';
            case 'PENDING':
            default:
                return 'warning';
        }
    };

    const renderStatus = (rowData) => {
        const status = resolveStatus(rowData);
        return <Tag value={status} severity={getStatusSeverity(status)} />;
    };

    
  const renderActions = (rowData) => {
        const isPending = !rowData.approved && !rowData.rejected;
        return (
            <div className="actions-container flex items-center justify-center gap-2">
                <div className="tooltip" data-tip="View Details">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setSelectedRequest(rowData);
                            setDetailsDialogVisible(true);
                        }}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
                {isPending && (
                    <>
                        <div className="tooltip" data-tip="Approve">
                            <button
                                className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                                onClick={() => handleActionOpen(rowData, 'APPROVE')}
                            >
                                <i className="pi pi-check text-lg"></i>
                            </button>
                        </div>
                        <div className="tooltip" data-tip="Reject">
                            <button
                                className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                                onClick={() => handleActionOpen(rowData, 'REJECT')}
                            >
                                <i className="pi pi-times text-lg"></i>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderBeneficiaryCard = (title, beneficiary) => {
        if (!beneficiary) {
            return (
                <div className="p-card p-3 h-full bg-light border-round">
                    <h5 className="font-semibold text-muted mb-2">{title}</h5>
                    <p className="text-muted">None / No beneficiary configured</p>
                </div>
            );
        }

        return (
            <div className="p-card p-3 h-full border-1 border-300 border-round">
                <h5 className="font-semibold mb-3 text-primary">{title}</h5>
                <div className="grid">
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">FIRST NAME</strong>
                        <span>{beneficiary.firstName || beneficiary.First_Name || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">LAST NAME</strong>
                        <span>{beneficiary.lastName || beneficiary.Surname || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">NATIONAL ID / ID NUMBER</strong>
                        <span>{beneficiary.idNumber || beneficiary.ID_Number || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">RELATIONSHIP</strong>
                        <span>{beneficiary.relationship || beneficiary.Relationship || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">PERCENTAGE SHARE</strong>
                        <span>{beneficiary.share || beneficiary.percentage || beneficiary.Percentage || 'N/A'}%</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">PHONE NUMBER</strong>
                        <span>{beneficiary.phoneNumber || beneficiary.Home_Tel || 'N/A'}</span>
                    </div>
                </div>
            </div>
        );
    };

    const columns = [
        { field: 'policyNumber', header: 'Policy Number', sortable: true },
        { field: 'createdAt', header: 'Requested Date', sortable: true, body: (rowData) => rowData.createdAt ? formatDateTime(rowData.createdAt) : 'N/A' },
        { field: 'operation', header: 'Type', sortable: true, body: (rowData) => rowData.operation || 'Update' },
        { field: 'status', header: 'Status', body: renderStatus, sortable: true },
        { field: 'actions', header: 'Actions', body: renderActions, style: { textAlign: 'center', width: '10rem' } },
    ];

    return (
        <div className="w-full">
            <Toast ref={toast} />

            {(loading || requests.length === 0) ? (
                <AppCard title="Beneficiary Change Requests">
                    {loading ? (
                        <div className="p-4">
                            <Skeleton className="mb-2" height="2rem"></Skeleton>
                            <Skeleton className="mb-2" height="2rem"></Skeleton>
                            <Skeleton className="mb-2" height="2rem"></Skeleton>
                            <Skeleton className="mb-2" height="2rem"></Skeleton>
                        </div>
                    ) : (
                        <div className="text-center p-5">
                            <i className="pi pi-inbox text-muted" style={{ fontSize: '3rem' }}></i>
                            <h4 className="mt-3 text-muted">No change requests found</h4>
                            <p className="text-muted">Currently, there are no pending beneficiary change requests to review.</p>
                        </div>
                    )}
                </AppCard>
            ) : (
                <DataTableCard
                    title="Beneficiary Change Requests"
                    exportName="Beneficiary Requests"
                    columns={columns}
                    rows={requests}
                    rowsPerPage={10}
                    emptyMessage="No matching requests found"
                    exportDisabled={false}
                />
            )}

            {/* Side-by-Side Details Dialog */}
            <Dialog
                header={`Beneficiary Change Details - Policy #${selectedRequest?.policyNumber}`}
                visible={detailsDialogVisible}
                style={{ width: '70vw' }}
                maximizable
                onHide={() => {
                    setDetailsDialogVisible(false);
                    setSelectedRequest(null);
                }}
            >
                {selectedRequest && (
                    <div className="p-fluid">
                        <div className="grid grid-nogutter mb-4">
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Request Status: </strong>
                                <Tag className="ml-1" value={resolveStatus(selectedRequest)} severity={getStatusSeverity(resolveStatus(selectedRequest))} />
                            </div>
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Request Date: </strong>
                                <span>{selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString() : 'N/A'}</span>
                            </div>
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Requested Type: </strong>
                                <Tag severity="info" value={selectedRequest.operation || 'UPDATE'} />
                            </div>
                        </div>

                        <div className="grid">
                            <div className="col-12 md:col-6">
                                {renderBeneficiaryCard("CURRENT BENEFICIARY STATE (BEFORE)", selectedRequest.operation === 'ADD' ? null : selectedRequest.currentBeneficiary)}
                            </div>
                            <div className="col-12 md:col-6">
                                {renderBeneficiaryCard("PROPOSED BENEFICIARY STATE (AFTER)", selectedRequest.details)}
                            </div>
                        </div>

                        {selectedRequest.requestReason && (
                            <div className="mt-3 p-card p-3 bg-light border-round">
                                <strong className="text-muted block text-xs mb-1">REQUEST REASON</strong>
                                <p className="m-0">{selectedRequest.requestReason}</p>
                            </div>
                        )}

                        {selectedRequest.reviewReason && (
                            <div className="mt-3 p-card p-3 bg-light border-round">
                                <strong className="text-muted block text-xs mb-1 text-danger">REVIEWER COMMENTS</strong>
                                <p className="m-0">{selectedRequest.reviewReason}</p>
                            </div>
                        )}

                        {!selectedRequest.approved && !selectedRequest.rejected && (
                            <div className="flex justify-content-end gap-3 mt-4">
                                <Button
                                    label="Reject"
                                    icon="pi pi-times"
                                    className="btn btn-error btn-outline p-button-danger p-button-outlined"
                                    onClick={() => {
                                        setDetailsDialogVisible(false);
                                        handleActionOpen(selectedRequest, 'REJECT');
                                    }}
                                />
                                <Button
                                    label="Accept"
                                    icon="pi pi-check"
                                    className="btn btn-success text-white p-button-success"
                                    onClick={() => {
                                        setDetailsDialogVisible(false);
                                        handleActionOpen(selectedRequest, 'APPROVE');
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Approval/Rejection Comments Dialog */}
            <Dialog
                header={`${actionType === 'APPROVE' ? 'Approve' : 'Reject'} Beneficiary Change Request`}
                visible={reviewDialogVisible}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="btn btn-ghost p-button-text"
                            disabled={submitting}
                            onClick={() => {
                                setReviewDialogVisible(false);
                                setSelectedRequest(null);
                            }}
                        />
                        <Button
                            label={actionType === 'APPROVE' ? 'Confirm Approval' : 'Confirm Rejection'}
                            icon="pi pi-check"
                            className={actionType === 'APPROVE' ? 'btn btn-success text-white p-button-success' : 'btn btn-error text-white p-button-danger'}
                            loading={submitting}
                            onClick={handleReviewSubmit}
                        />
                    </div>
                }
                onHide={() => {
                    setReviewDialogVisible(false);
                    setSelectedRequest(null);
                }}
            >
                <div className="p-fluid">
                    {actionType === 'REJECT' && (
                        <div className="p-message p-message-warn p-3 mb-3 border-round">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            <strong>Warning:</strong> You are about to reject this request. A comment explaining the reason is required.
                        </div>
                    )}
                    <div className="field">
                        <label htmlFor="comments" className="font-semibold block mb-2">Reviewer Comments / Reason</label>
                        <InputTextarea
                            id="comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={5}
                            autoResize
                            placeholder={actionType === 'APPROVE' ? 'Add any optional comments here...' : 'Explain the reason for rejection here (required)...'}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default BeneficiaryRequestsTable;
